import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Ticket, ArrowRight, Loader2, Search, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import PurchasedLeadCard from '@/components/contractor/PurchasedLeadCard';
import { PendingPaidLead, formatLeadDate } from '@/lib/myLeads';
import { MY_LEADS_PATH } from '@/data/contractorNav';
import { useMyLeads } from '@/hooks/useMyLeads';

/** Paid for a specific lead, not unlocked yet — with a one-tap finish. */
export const PendingPaidBanner: React.FC<{
  pending: PendingPaidLead[];
  onUnlock: (listingId: string) => Promise<{ ok: boolean; reason: string }>;
}> = ({ pending, onUnlock }) => {
  const { lang, path } = useI18n();
  const es = lang === 'es';
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  if (!pending.length) return null;

  const run = async (id: string) => {
    setBusy(id);
    setMsg('');
    const r = await onUnlock(id);
    setBusy(null);
    if (!r.ok && r.reason !== 'already_owned') {
      setMsg(
        /sold_out|unavailable|closed/.test(r.reason)
          ? es
            ? 'Ese proyecto se llenó durante su pago. Su crédito sigue en su cuenta para usarlo en otro proyecto.'
            : 'That project filled up during your payment. Your credit is still in your account to use on another project.'
          : /no_credit|insufficient/.test(r.reason)
          ? es
            ? 'Su pago aún se está confirmando. Se desbloqueará automáticamente en unos minutos.'
            : 'Your payment is still being confirmed. It will unlock automatically within a few minutes.'
          : es
          ? 'No pudimos completar el desbloqueo. Intente de nuevo en un momento.'
          : 'We could not finish the unlock. Please try again in a moment.',
      );
    }
  };

  return (
    <div className="mb-5 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5">
      <p className="flex items-center gap-2 text-base font-extrabold text-amber-900">
        <Clock className="h-5 w-5" />
        {es ? 'Pago recibido — terminando su lead' : 'Payment received — finishing your lead'}
      </p>
      <p className="mt-1 text-sm text-amber-800">
        {es
          ? 'Pagó por estos proyectos. Los desbloqueamos automáticamente; si alguno sigue aquí, toque Desbloquear ahora.'
          : 'You paid for these projects. We unlock them automatically — if one is still listed, tap Unlock now.'}
      </p>
      <ul className="mt-3 space-y-2">
        {pending.map((p) => (
          <li key={p.key} className="flex flex-col gap-2 rounded-xl bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm">
              <p className="font-bold text-slate-900">
                {p.slug ? (
                  <Link to={path(`/jobs/${p.slug}`)} className="hover:text-orange-600">
                    {p.slug.replace(/-/g, ' ')}
                  </Link>
                ) : es ? 'Proyecto pagado' : 'Paid project'}
              </p>
              <p className="text-xs text-slate-500">
                {es ? 'Pagado' : 'Paid'} {formatLeadDate(p.paidAt, lang)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => run(p.listingId)}
              disabled={busy === p.listingId}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {busy === p.listingId ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {es ? 'Desbloquear ahora' : 'Unlock now'}
            </button>
          </li>
        ))}
      </ul>
      {msg && <p className="mt-3 text-sm font-semibold text-amber-900">{msg}</p>}
    </div>
  );
};

/**
 * DASHBOARD — "My Purchased Leads" is the first thing a contractor sees under
 * their balance. Latest leads with contact details inline + a big link to the
 * full list.
 */
const PurchasedLeadsPreview: React.FC = () => {
  const { lang, path } = useI18n();
  const es = lang === 'es';
  const { leads, pending, loading, error, justUnlocked, reload, setStage, unlockPending } = useMyLeads();
  const shown = leads.slice(0, 3);

  return (
    <section className="mb-12 rounded-3xl border-2 border-orange-200 bg-gradient-to-br from-orange-50 via-white to-white p-5 sm:p-7">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-extrabold text-slate-900">
            <Ticket className="h-6 w-6 text-orange-500" />
            {es ? 'Mis Leads Comprados' : 'My Purchased Leads'}
            {!loading && (
              <span className="rounded-full bg-orange-500 px-2.5 py-0.5 text-sm font-bold text-white">{leads.length}</span>
            )}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            {es
              ? 'Cada lead que paga queda aquí para siempre, con los datos de contacto.'
              : 'Every lead you pay for stays here permanently, with the contact details.'}
          </p>
        </div>
        <Link
          to={path(MY_LEADS_PATH)}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-slate-900 px-5 text-sm font-bold text-white hover:bg-slate-800"
        >
          {es ? 'Ver todos mis leads' : 'View all my leads'} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {justUnlocked > 0 && (
        <p className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm font-bold text-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {es
            ? justUnlocked === 1
              ? 'Terminamos de desbloquear un lead que ya había pagado.'
              : `Terminamos de desbloquear ${justUnlocked} leads que ya había pagado.`
            : justUnlocked === 1
            ? 'We finished unlocking a lead you had already paid for.'
            : `We finished unlocking ${justUnlocked} leads you had already paid for.`}
        </p>
      )}

      <div className="mt-5">
        <PendingPaidBanner pending={pending} onUnlock={unlockPending} />

        {loading ? (
          <p className="flex items-center gap-2 py-8 text-sm font-semibold text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> {es ? 'Cargando sus leads…' : 'Loading your leads…'}
          </p>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
            {es ? 'No pudimos cargar sus leads.' : 'We could not load your leads.'}{' '}
            <button onClick={reload} className="inline-flex items-center gap-1 font-bold underline">
              <RefreshCw className="h-3.5 w-3.5" /> {es ? 'Reintentar' : 'Retry'}
            </button>
          </div>
        ) : shown.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
            <p className="text-sm text-slate-600">
              {es
                ? 'Aún no ha comprado leads. Cuando lo haga, aparecerán aquí con el nombre, teléfono y correo del cliente.'
                : "You haven't purchased any leads yet. When you do, they appear here with the customer's name, phone and email."}
            </p>
            <Link
              to={path('/jobs')}
              className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-orange-500 px-5 text-sm font-bold text-white hover:bg-orange-600"
            >
              <Search className="h-4 w-4" /> {es ? 'Buscar leads' : 'Find leads'}
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {shown.map((l) => (
              <PurchasedLeadCard key={l.id} lead={l} onStage={setStage} compact />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PurchasedLeadsPreview;
